// pages/receipts/ReceiptFormModal.jsx
// Record a receipt — money RECEIVED from a customer, against one of their orders.
// Named distinctly from orders/ReceiptModal.jsx (which prints the receipt document).
import { useEffect, useMemo, useState } from "react";
import { ordersAPI, receiptsAPI } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { Modal, Spinner } from "../../components/ui";
import { PAYMENT_MODES, todayISO } from "../../utils/helpers";

export default function ReceiptFormModal({ onClose, onSaved }) {
  const toast = useToast();

  const [orders, setOrders]         = useState([]);
  const [loadingOrders, setLoading] = useState(true);
  const [orderSearch, setOrderSearch] = useState("");
  const [saving, setSaving]         = useState(false);

  const [form, setForm] = useState({
    orderId: "",
    amount:  "",
    kasar:   "",
    mode:    PAYMENT_MODES[0],
    date:    todayISO(),
    remarks: "",
  });

  useEffect(() => {
    setLoading(true);
    ordersAPI
      .list()
      .then((d) => setOrders(Array.isArray(d) ? d : []))
      .catch(() => toast("Failed to load orders", "error"))
      .finally(() => setLoading(false));
  }, []);

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(
      (o) =>
        String(o.id ?? "").toLowerCase().includes(q) ||
        (o.customerName ?? "").toLowerCase().includes(q)
    );
  }, [orders, orderSearch]);

  const selectedOrder = orders.find((o) => String(o.id) === String(form.orderId));

  // mapOrder() carries OM_TotalReceipt as `received` and OM_Discount as `discount`, so
  // the outstanding amount can be shown up front instead of making the user work it out.
  // Both are subtracted because that is exactly the condition Get_Receipt.php closes on:
  // OM_TotalReceipt + OM_Discount >= OM_TotalReceivable. Counting only `received` would
  // overstate what is still owed on any order that already has a discount.
  const outstanding = selectedOrder
    ? Math.max(
        0,
        (selectedOrder.amount || 0) - (selectedOrder.received || 0) - (selectedOrder.discount || 0)
      )
    : 0;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Get_Receipt.php credits BOTH values against the order — OM_TotalReceipt += Amount
  // and OM_Discount += Kasar — and closes it once the running total covers
  // OM_TotalReceivable. So kasar settles the order just as much as cash does, and the
  // remaining figure has to account for both.
  const amtNum      = Number(form.amount) || 0;
  const kasarNum    = Number(form.kasar)  || 0;
  const remaining   = Math.max(0, outstanding - amtNum - kasarNum);
  const willClose    = selectedOrder && outstanding > 0 && amtNum + kasarNum >= outstanding;
  const overSettled  = selectedOrder && outstanding > 0 && amtNum + kasarNum > outstanding;

  const handleSave = async () => {
    if (!form.orderId)              return toast("Please select an order", "error");
    const amt = Number(form.amount);
    if (!amt || amt <= 0)          return toast("Enter a valid amount", "error");
    if (kasarNum < 0)              return toast("Kasar can't be negative", "error");
    if (!form.date)                return toast("Please pick a date", "error");

    setSaving(true);
    try {
      await receiptsAPI.create({
        orderId:    form.orderId,
        customerId: selectedOrder?.customerId ?? null,
        amount:     amt,
        kasar:      kasarNum,
        mode:       form.mode,
        date:       form.date,
        remarks:    form.remarks.trim(),
      });
      toast("Receipt recorded 🧾");
      onSaved?.();
    } catch (e) {
      console.error("[ReceiptFormModal] save error:", e);
      // The endpoint rolls back and says why ("Order not found…"), so surface it.
      toast(e?.message || "Failed to save receipt", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Record Receipt"
      subtitle="Money received against an order"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loadingOrders}>
            {saving ? <Spinner /> : "Save Receipt"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        {/* Order picker */}
        <div className="form-group form-full">
          <label className="form-label">Order</label>
          {orders.length > 8 && (
            <div className="search-bar" style={{ marginBottom: 8 }}>
              <span className="search-icon">🔍</span>
              <input
                className="form-input"
                placeholder="Filter orders by ID or customer…"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
              />
            </div>
          )}
          <select
            className="form-select"
            value={form.orderId}
            onChange={(e) => set("orderId", e.target.value)}
            disabled={loadingOrders}
          >
            <option value="">{loadingOrders ? "Loading orders…" : "Select an order"}</option>
            {filteredOrders.map((o) => (
              <option key={o.id} value={o.id}>
                #{o.id} — {o.customerName || "Customer"}{o.amount ? ` (₹${o.amount.toLocaleString()})` : ""}
              </option>
            ))}
          </select>
          {selectedOrder && (
            <div className="td-muted" style={{ marginTop: 6, fontSize: 12 }}>
              Customer: <strong>{selectedOrder.customerName || "—"}</strong>
              {selectedOrder.amount ? ` · Order total ₹${selectedOrder.amount.toLocaleString()}` : ""}
              {selectedOrder.amount ? ` · Outstanding ₹${outstanding.toLocaleString()}` : ""}
              {(amtNum > 0 || kasarNum > 0) && (
                <div style={{ marginTop: 4 }}>
                  {willClose ? (
                    <span style={{ color: "var(--green)" }}>
                      This settles the order in full — it will be marked closed.
                      {overSettled &&
                        ` (₹${(amtNum + kasarNum - outstanding).toLocaleString()} more than outstanding)`}
                    </span>
                  ) : (
                    <>Remaining after this: <strong>₹{remaining.toLocaleString()}</strong></>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Amount */}
        <div className="form-group">
          <label className="form-label">Amount (₹)</label>
          <input
            type="number"
            min="0"
            className="form-input"
            placeholder="0"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
          />
        </div>

        {/* Kasar — the discount written off. Goes to RD_Kasar and is added to
            OM_Discount, so it counts towards settling the order. */}
        <div className="form-group">
          <label className="form-label">Kasar (₹) <span className="form-;abel">(optional)</span></label>
          <input
            type="number"
            min="0"
            className="form-input"
            placeholder="0"
            value={form.kasar}
            onChange={(e) => set("kasar", e.target.value)}
          />
        </div>

        {/* Mode */}
        <div className="form-group">
          <label className="form-label">Payment Mode</label>
          <select className="form-select" value={form.mode} onChange={(e) => set("mode", e.target.value)}>
            {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Date */}
        <div className="form-group">
          <label className="form-label">Date</label>
          <input type="date" className="form-input" value={form.date} onChange={(e) => set("date", e.target.value)} />
        </div>

        {/* Remarks */}
        <div className="form-group form-full">
          <label className="form-label">Remarks <span className="form-label">(optional)</span></label>
          <textarea
            className="form-textarea"
            rows={2}
            placeholder="Any note about this receipt…"
            value={form.remarks}
            onChange={(e) => set("remarks", e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
